import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {Search, MapPin, Briefcase, Store, User} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { lumi } from '@/lib/lumi';

interface Usuario {
  _id: string;
  userId: string;
  nome?: string;
  email?: string;
  profileImageUrl?: string;
  bannerUrl?: string;
  profileType?: string;
  cidade?: string;
  setor?: string;
}

function BuscarUsuarios() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'trabalhador' | 'restaurante'>('todos');

  useEffect(() => {
    setLoading(true);
    lumi.entities.user_profiles.list({ sort: { createdAt: -1 } }).
    then((res: any) => {
      setUsuarios(res?.list || []);
    }).
    catch((e: any) => {
      console.error('Erro ao carregar usuarios:', e);
    }).
    finally(() => setLoading(false));
  }, []);

  const usuariosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return usuarios.filter((u) => {
      if (filtroTipo !== 'todos' && (u?.profileType || 'trabalhador') !== filtroTipo) {
        return false;
      }
      if (!termo) return true;
      const nome = (u?.nome || '').toLowerCase();
      const email = (u?.email || '').toLowerCase();
      const setor = (u?.setor || '').toLowerCase();
      const cidade = (u?.cidade || '').toLowerCase();
      return (
        nome.includes(termo) ||
        email.includes(termo) ||
        setor.includes(termo) ||
        cidade.includes(termo));

    });
  }, [usuarios, busca, filtroTipo]);

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Search className="text-[#E53935]" size={32} />
            Pesquisar usuários
          </h1>
          <p className="text-gray-400 mt-2">Encontre trabalhadores e estabelecimentos na plataforma.</p>
        </div>

        {/* Barra de busca */}
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, email, setor ou cidade..."
              className="w-full bg-[#2A2A2A] border border-[#4E342E] text-white placeholder-gray-500 rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-[#E53935] transition-all" />

          </div>
          <div className="flex gap-2">
            {([
            { key: 'todos', label: 'Todos' },
            { key: 'trabalhador', label: 'Trabalhadores' },
            { key: 'restaurante', label: 'Estabelecimentos' }] as
            const).map((opt) =>
            <button
              key={opt.key}
              onClick={() => setFiltroTipo(opt.key)}
              className={`px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
              filtroTipo === opt.key ?
              'bg-[#E53935] text-white' :
              'bg-[#2A2A2A] text-gray-300 border border-[#4E342E] hover:bg-[#3A3A3A]'}`
              }>

                {opt.label}
              </button>
            )}
          </div>
        </div>

        {/* Resultados */}
        {loading ?
        <div className="text-center text-gray-400 py-20">Carregando usuários...</div> :
        usuariosFiltrados.length === 0 ?
        <div className="text-center text-gray-400 py-20">
            <User className="mx-auto mb-4 text-gray-600" size={48} />
            Nenhum usuário encontrado.
          </div> :

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {usuariosFiltrados.map((u) => {
            const isRestaurante = (u?.profileType || 'trabalhador') === 'restaurante';
            const nome = u?.nome || u?.email?.split('@')[0] || 'Usuário';
            const inicial = nome.charAt(0).toUpperCase();
            return (
              <Link
                key={u._id}
                to={`/perfil/${u.userId}`}
                className="bg-[#2A2A2A] rounded-2xl overflow-hidden border border-[#4E342E] hover:border-[#E53935] transition-all group flex flex-col min-h-[330px]">

                  <div className="h-32 bg-gradient-to-r from-[#3E2723] to-[#6D4C41] relative">
                    {u?.bannerUrl &&
                  <img src={u.bannerUrl} alt="Banner" className="w-full h-full object-cover" />
                  }
                  </div>
                  <div className="px-5 pb-5 flex-1">
                    <div className="-mt-12 mb-3 relative z-10">
                      {u?.profileImageUrl ?
                    <img
                      src={u.profileImageUrl}
                      alt={nome}
                      className="w-24 h-24 rounded-full object-cover border-4 border-[#2A2A2A] bg-[#1A1A1A] shadow-lg" /> :


                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#C62828] to-[#E53935] flex items-center justify-center text-white text-3xl font-bold border-4 border-[#2A2A2A] shadow-lg">
                          {inicial}
                        </div>
                    }
                    </div>
                    <h3 className="text-white font-bold text-lg truncate group-hover:text-[#E53935] transition-colors">{nome}</h3>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1.5">
                      {isRestaurante ?
                    <><Store size={13} /> <span>Estabelecimento</span></> :

                    <><Briefcase size={13} /> <span>Trabalhador</span></>
                    }
                    </div>
                    {u?.cidade &&
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-1">
                        <MapPin size={13} /> <span className="truncate">{u.cidade}</span>
                      </div>
                  }
                    {u?.setor &&
                  <span className="inline-block mt-3 text-[11px] bg-[#3E2723] text-[#FFCCBC] px-2.5 py-1 rounded-full">
                        {u.setor}
                      </span>
                  }
                  </div>
                </Link>);

          })}
          </div>
        }
      </div>

      <Footer />
    </div>);

}

export default BuscarUsuarios;
